import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Add, DeleteOutline, VpnKey, Visibility, VisibilityOff } from '@mui/icons-material';
import { newImportService } from '../../services/newImportService';

/**
 * Access Tokens admin: manage ADO PATs (pool; random selection in use).
 */
const AccessTokensAdmin = () => {
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);

  const [adoSummary, setAdoSummary] = useState(null);
  const [adoConfig, setAdoConfig] = useState(null);
  const [adoPrimaryPat, setAdoPrimaryPat] = useState('');
  const [adoAdditionalEntries, setAdoAdditionalEntries] = useState([]);
  const [adoSaving, setAdoSaving] = useState(false);
  const [showPatValues, setShowPatValues] = useState(false);

  const loadAdo = async () => {
    try {
      const [summaryRes, configRes] = await Promise.all([
        newImportService.getAccessTokensAdo(),
        newImportService.getAdoConfig()
      ]);
      if (summaryRes.success && summaryRes.data) {
        const d = summaryRes.data;
        setAdoSummary(d);
        const labels = d.additionalPatLabels || [];
        const values = d.additionalPatValues || [];
        setAdoAdditionalEntries(
          labels.map((l, i) => ({ label: l || '', pat: values[i] != null ? values[i] : '' }))
        );
        setAdoPrimaryPat(d.primaryPatValue != null ? d.primaryPatValue : '');
      }
      if (configRes.success && configRes.data) {
        setAdoConfig(configRes.data);
      }
    } catch (e) {
      console.error('Load ADO access tokens failed:', e);
      setMessage({ type: 'error', text: 'Failed to load ADO tokens.' });
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessage({ type: '', text: '' });
    loadAdo().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const showSuccess = (text) => {
    setMessage({ type: 'success', text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const showError = (text) => setMessage({ type: 'error', text });

  const handleSaveAdo = async () => {
    if (!adoConfig) return;
    setAdoSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = {
        orgUrl: adoConfig.orgUrl,
        project: adoConfig.project,
        queryUrl: adoConfig.queryUrl,
        epicWiql: adoConfig.epicWiql
      };
      if (adoPrimaryPat && String(adoPrimaryPat).trim()) payload.pat = adoPrimaryPat.trim();
      const withPat = adoAdditionalEntries.filter((e) => e.pat && String(e.pat).trim());
      payload.additionalPats = withPat.map((e) => e.pat.trim());
      payload.additionalPatLabels = withPat.map((e) => (e.label && String(e.label).trim()) || '');
      const res = await newImportService.updateAdoConfig(payload);
      if (res.success) {
        setAdoPrimaryPat('');
        await loadAdo();
        showSuccess('ADO PATs saved. A random PAT from the pool is used for each request.');
      } else {
        showError(res.message || 'Failed to save ADO PATs.');
      }
    } catch (e) {
      showError(e.response?.data?.message || e.message || 'Failed to save ADO PATs.');
    } finally {
      setAdoSaving(false);
    }
  };

  const addAdoAdditional = () => setAdoAdditionalEntries((prev) => [...prev, { label: '', pat: '' }]);
  const removeAdoAdditional = (index) => setAdoAdditionalEntries((prev) => prev.filter((_, i) => i !== index));
  const updateAdoAdditional = (index, field, value) => {
    setAdoAdditionalEntries((prev) => prev.map((e, i) => (i === index ? { ...e, [field]: value } : e)));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Access Tokens
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage ADO personal access tokens. A random token from the pool is used for each request to spread load and reduce rate limits.
      </Typography>

      {message.text && (
        <Alert severity={message.type === 'error' ? 'error' : 'success'} onClose={() => setMessage({ type: '', text: '' })} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKey fontSize="small" /> ADO personal access tokens
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Primary PAT and additional PATs are stored here. Visible only to admins; use the eye icon to show or hide values.
          </Typography>
          {adoSummary && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Primary: {adoSummary.primarySet ? 'Set' : 'Not set'}. Additional: {adoSummary.additionalCount ?? 0} PAT(s).
            </Typography>
          )}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              type={showPatValues ? 'text' : 'password'}
              label="Primary PAT"
              value={adoPrimaryPat}
              onChange={(e) => setAdoPrimaryPat(e.target.value)}
              placeholder={adoSummary?.primarySet ? 'Leave blank to keep current' : 'Enter primary PAT'}
              size="small"
              fullWidth
              sx={{ maxWidth: 400 }}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKey fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showPatValues ? 'Hide PAT values' : 'Show PAT values (admin only)'}>
                      <IconButton
                        size="small"
                        onClick={() => setShowPatValues((v) => !v)}
                        aria-label={showPatValues ? 'Hide PATs' : 'Show PATs'}
                      >
                        {showPatValues ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          </Box>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Additional PATs</Typography>
          {adoAdditionalEntries.map((entry, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
              <TextField
                label="Label"
                value={entry.label}
                onChange={(e) => updateAdoAdditional(index, 'label', e.target.value)}
                placeholder="e.g. Team A"
                size="small"
                sx={{ minWidth: 140, flex: '1 1 140px' }}
                autoComplete="off"
              />
              <TextField
                type={showPatValues ? 'text' : 'password'}
                label="PAT"
                value={entry.pat}
                onChange={(e) => updateAdoAdditional(index, 'pat', e.target.value)}
                placeholder="••••••••"
                size="small"
                sx={{ minWidth: 180, flex: '2 1 180px' }}
                autoComplete="off"
              />
              <Tooltip title="Remove this PAT">
                <IconButton onClick={() => removeAdoAdditional(index)} color="secondary" size="small" aria-label={`Remove PAT ${index + 1}`}>
                  <DeleteOutline />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
            <Button variant="outlined" size="small" startIcon={<Add />} onClick={addAdoAdditional}>
              Add PAT
            </Button>
            <Button variant="contained" size="small" onClick={handleSaveAdo} disabled={adoSaving}>
              {adoSaving ? 'Saving...' : 'Save ADO PATs'}
            </Button>
          </Box>
        </Paper>
    </Box>
  );
};

export default AccessTokensAdmin;
